/**
 * Shared limits and MIME allowlist for POST /api/albums/:albumId/upload-photos
 * (must stay aligned with server/routes/albumRoutes.js UPLOAD_PHOTOS_ALLOWED_MIMES).
 */

export const MAX_ALBUM_UPLOAD_FILES = 10;

export const MAX_ALBUM_PHOTO_BYTES = 150 * 1024 * 1024;

/** Server-accepted MIME types for album media upload. */
export const ALBUM_UPLOAD_ALLOWED_MIMES: readonly string[] = [
   'image/jpeg',
   'image/jpg',
   'image/png',
   'image/gif',
   'image/webp',
   'image/bmp',
   'image/tiff',
   'image/svg+xml',
   'video/mp4',
   'video/webm',
   'video/quicktime',
   'video/x-msvideo',
   'video/ogg',
];

/** Image types we send as-is (no Jimp re-encode). HEIC is not server-allowed; convert separately. */
export const ALBUM_UPLOAD_PASS_THROUGH_IMAGE_MIMES: readonly string[] = [
   'image/jpeg',
   'image/jpg',
   'image/png',
   'image/gif',
   'image/webp',
   'image/bmp',
   'image/tiff',
   'image/svg+xml',
];

const EXT_TO_MIME: Record<string, string> = {
   jpg: 'image/jpeg',
   jpeg: 'image/jpeg',
   png: 'image/png',
   gif: 'image/gif',
   webp: 'image/webp',
   bmp: 'image/bmp',
   tiff: 'image/tiff',
   tif: 'image/tiff',
   svg: 'image/svg+xml',
   mp4: 'video/mp4',
   webm: 'video/webm',
   mov: 'video/quicktime',
   avi: 'video/x-msvideo',
   ogv: 'video/ogg',
   ogg: 'video/ogg',
};

/**
 * Resolve canonical MIME for album upload: trust file.type if allowed, else map extension.
 */
export function resolveAlbumUploadMime(file: File): string | null {
   const t = (file.type || '').toLowerCase().trim();
   if (t && ALBUM_UPLOAD_ALLOWED_MIMES.includes(t)) {
      return t === 'image/jpg' ? 'image/jpeg' : t;
   }
   const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
   const fromExt = EXT_TO_MIME[ext];
   if (fromExt && ALBUM_UPLOAD_ALLOWED_MIMES.includes(fromExt)) {
      return fromExt;
   }
   return null;
}

export function isAlbumUploadAllowedMime(mime: string | null): boolean {
   if (!mime) return false;
   const n = mime.toLowerCase();
   return ALBUM_UPLOAD_ALLOWED_MIMES.includes(n === 'image/jpg' ? 'image/jpeg' : n);
}
